import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import '../styles/CursorCircle.css';

const RADIUS = 37.5;

const CursorCircle = () => {
    const rawX = useMotionValue(-300);
    const rawY = useMotionValue(-300);
    const turbRef = useRef(null);

    const smoothX = useSpring(rawX, { stiffness: 180, damping: 22, mass: 0.5 });
    const smoothY = useSpring(rawY, { stiffness: 180, damping: 22, mass: 0.5 });

    // Center the circle on the cursor
    const x = useTransform(smoothX, v => v - RADIUS);
    const y = useTransform(smoothY, v => v - RADIUS);

    useEffect(() => {
        const move = (e) => {
            rawX.set(e.clientX);
            rawY.set(e.clientY);
        };
        window.addEventListener('mousemove', move);
        return () => window.removeEventListener('mousemove', move);
    }, [rawX, rawY]);

    // Slowly animate turbulence baseFrequency to create fluid, living distortion
    useEffect(() => {
        let frame;
        const animate = (timestamp) => {
            const t = timestamp / 3000;
            if (turbRef.current) {
                const bfX = (0.013 + 0.005 * Math.sin(t)).toFixed(4);
                const bfY = (0.013 + 0.005 * Math.cos(t * 0.7)).toFixed(4);
                turbRef.current.setAttribute('baseFrequency', `${bfX} ${bfY}`);
            }
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <>
            <svg
                width="0"
                height="0"
                aria-hidden="true"
                style={{ position: 'absolute', overflow: 'hidden' }}
            >
                <defs>
                    <filter
                        id="glass-lens"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                        colorInterpolationFilters="sRGB"
                    >
                        <feTurbulence
                            ref={turbRef}
                            type="fractalNoise"
                            baseFrequency="0.013 0.013"
                            numOctaves="3"
                            seed="4"
                            result="noise"
                        />
                        <feDisplacementMap
                            in="SourceGraphic"
                            in2="noise"
                            scale="32"
                            xChannelSelector="R"
                            yChannelSelector="G"
                        />
                    </filter>
                </defs>
            </svg>

            <motion.div className="cursor-circle" style={{ x, y }} />
        </>
    );
};

export default CursorCircle;
